import React from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function CourseCoverage() {
  const { ref: headRef, isVisible: headVisible } = useScrollAnimation();
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: bottomLeftRef, isVisible: bottomLeftVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: bottomRightRef, isVisible: bottomRightVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="py-24 lg:py-32 bg-[#FFF9F5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={headRef}
          className={`text-center mb-16 transition-all duration-700 ease-out ${
            headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-xs uppercase tracking-widest text-[#BA3D03] font-semibold">
            LEARNING OUTCOMES
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0E0503] mt-4 mb-6">
            What the course covers
          </h2>
          <p className="text-lg text-[#3D1203] max-w-3xl mx-auto leading-relaxed">
            Practical capability building to interpret the seven Better Placed objectives through a cultural lens and apply them in delivery.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 overflow-hidden">
          {/* Card 01 */}
          <div
            ref={leftRef}
            className={`bg-[#3D1203] rounded-2xl p-6 lg:p-10 text-white transition-all duration-300 ease-out cursor-default hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#3D1203]/40 hover:scale-[1.04] ${
              leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <span className="text-5xl lg:text-6xl font-bold text-[#E48523]/30 block mb-3 sm:mb-4">01</span>
            <h3 className="text-xl font-bold mb-4 sm:mb-6">
              Understanding Country and Cultural Protocols
            </h3>
            <ul className="space-y-3 text-white/90">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#E48523] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Understand Country as a living cultural system across Earth, Water and Sky</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#E48523] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Understand the Indigenous concept of Country and its significance in design</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#E48523] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Understanding both the Physical and Spiritual connections to Country for Indigenous peoples</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#E48523] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Apply cultural safety principles in project environments</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#E48523] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Recognise and avoid tokenistic approaches</span>
              </li>
            </ul>
          </div>

          {/* Card 02 */}
          <div
            ref={rightRef}
            className={`bg-[#E8C580] rounded-2xl p-6 lg:p-10 transition-all duration-300 ease-out cursor-default hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#E8C580]/50 hover:scale-[1.04] delay-150 ${
              rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <span className="text-5xl lg:text-6xl font-bold text-[#BA3D03]/30 block mb-3 sm:mb-4">02</span>
            <h3 className="text-xl font-bold text-[#0E0503] mb-6">
              The Four Phase Country-centred Framework
            </h3>
            <ul className="space-y-3 text-[#0E0503]/90">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#BA3D03] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Starting with Country</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#BA3D03] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Imagining with Country</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#BA3D03] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Shaping Country</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#BA3D03] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Caring for Country</span>
              </li>
            </ul>
            <p className="text-base text-[#0E0503]/80 mt-4">
              A practical model to guide Country-led decision making across the project lifecycle.
            </p>
          </div>

          {/* Card 03 */}
          <div
            ref={bottomLeftRef}
            className={`bg-[#FEC796] rounded-2xl p-6 lg:p-10 transition-all duration-700 ease-out cursor-default hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#FEC796]/50 hover:scale-[1.04] ${
              bottomLeftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <span className="text-5xl lg:text-6xl font-bold text-[#BA3D03]/30 block mb-3 sm:mb-4">03</span>
            <h3 className="text-xl font-bold text-[#0E0503] mb-6">
              Engagement, ICIP and Culturally Safe Practice
            </h3>
            <ul className="space-y-3 text-[#0E0503]/90">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#BA3D03] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Structure meaningful engagement with Traditional Custodians and Indigenous organisations</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#BA3D03] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Determine appropriate levels of community involvement</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#BA3D03] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Protect Indigenous Cultural and Intellectual Property appropriately</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#BA3D03] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Embed Indigenous leadership and co-design principles</span>
              </li>
            </ul>
          </div>

          {/* Card 04 */}
          <div
            ref={bottomRightRef}
            className={`bg-[#512A05] rounded-2xl p-6 lg:p-10 transition-all duration-700 ease-out cursor-default hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#512A05]/40 hover:scale-[1.04] delay-150 ${
              bottomRightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <span className="text-5xl lg:text-6xl font-bold text-[#D6A070]/30 block mb-3 sm:mb-4">04</span>
            <h3 className="text-xl font-bold text-white mb-6">
              Country-led Design Philosophy
            </h3>
            <ul className="space-y-3 text-white/90">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#D6A070] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Healthy Country</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#D6A070] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Healthy People</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#D6A070] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Belonging</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#D6A070] text-lg leading-5 mt-0.5 flex-shrink-0"></i>
                <span className="text-sm leading-relaxed">Shared Knowledge</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Tertiary CTA link */}
        <div className="text-center mt-12">
          <button
            onClick={() => {
              if (window.REACT_APP_NAVIGATE) {
                window.REACT_APP_NAVIGATE('/course-overview');
              }
            }}
            className="text-[#BA3D03] hover:text-[#3D1203] text-sm font-medium transition-colors cursor-pointer underline underline-offset-4 decoration-[#BA3D03]/40 hover:decoration-[#3D1203]/80 whitespace-nowrap"
          >
            See the full module breakdown →
          </button>
        </div>
      </div>
    </section>
  );
}
